import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withTranslation } from 'react-i18next'
import i18next from 'i18next'

import { ReactComponent as OrientationLandscapeIcon } from '../../../assets/images/icons/orientation-landscape.svg'
import { ReactComponent as OrientationPortraitIcon } from '../../../assets/images/icons/orientation-portrait.svg'
import Button from '../../Common/Buttons/Button/Button'
import Modal from '../../Common/Modal/Modal'
import Select from '../../Common/Select/Select'
import TileButton from '../../Common/Buttons/TileButton/TileButton'
import Tooltip from '../../Tooltip/Tooltip'
import InputNumeric from '../../Common/InputNumeric/InputNumeric'

import {
  updateCropperValues,
  setCropperActive,
  setCropperChangedStatus,
  setActivePresetById,
  toggleCropperRatioLock,
  setCropperModal,
} from '../../../store/actions/cropper.actions'
import { addNewSetting, updateSettingsIds } from '../../../store/actions/settings.actions'
import { findPresetById, getLastPhysicsSetting } from '../../../helpers/util'
import { sendMetric } from '../../../helpers/metric'

import { CROPPER_PRESET_TYPES, mapControls } from '../../../constants/controls'
import { ReactComponent as UnlockIcon } from '../../../assets/images/icons/i-unlock.svg'
import { ReactComponent as LockIcon } from '../../../assets/images/icons/i-lock.svg'
import IconButton from '../../Common/Buttons/IconButton/IconButton'

import './CropperControl.scss'

const buildPresetOptions = (t) => {
  return mapControls.cropper.map(({ id, name, w, h, type }) => {
    return {
      value: id,
      label: t(`cropper-presets.${name}`),
      name,
      id,
      w,
      h,
      type
    }
  })
}

class CropperControl extends Component {
  // TODO: зарефакторить propTypes на более точные определения типов объектов
  static propTypes = {
    imageSrc: PropTypes.string.isRequired,
    imageData: PropTypes.object.isRequired,
    cropper: PropTypes.object.isRequired,
    cropperTo: PropTypes.string.isRequired,
    updateCropperValues: PropTypes.func.isRequired,
    setCropperActive: PropTypes.func.isRequired,
    setCropperChangedStatus: PropTypes.func.isRequired,
    setActivePresetById: PropTypes.func.isRequired,
    toggleCropperRatioLock: PropTypes.func.isRequired,
    setCropperModal: PropTypes.func.isRequired,
    showCropperModal: PropTypes.bool.isRequired,
    canvas: PropTypes.object.isRequired,
    settingsList: PropTypes.array.isRequired,
    currentSettingId: PropTypes.number.isRequired,
    addNewSetting: PropTypes.func.isRequired,
    updateSettingsIds: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    history: PropTypes.any,
  }

  state = {
    presets: []
  }

  constructor(props) {
    super(props)
    const { t } = props

    this.state.presets = buildPresetOptions(t)
  }

  componentDidMount() {
    const { setCropperActive } = this.props

    setCropperActive(true)

    i18next.on('languageChanged', this.onLanguageChanged)
  }

  componentDidUpdate(prevProps) {
    const { cropper, updateCropperValues, canvas, settingsList, currentSettingId } = this.props

    const currCropSetting = getLastPhysicsSetting(settingsList, currentSettingId)
    const prevCropSetting = getLastPhysicsSetting(prevProps.settingsList, prevProps.currentSettingId)

    if (currCropSetting !== prevCropSetting) {
      const newCropper = {
        ...cropper,
        x: canvas.x,
        y: canvas.y,
        width: canvas.width,
        height: canvas.height,
        changed: false
      }
      updateCropperValues(newCropper)
    }
  }

  componentWillUnmount() {
    const { cropper, updateCropperValues, setCropperActive, setActivePresetById, setCropperChangedStatus } = this.props
    setCropperActive(false)

    const newCropper = {
      ...cropper,
      id: 0,
    }

    updateCropperValues(newCropper)
    setCropperChangedStatus(false)
    setActivePresetById(0)

    i18next.off('languageChanged', this.onLanguageChanged)
  }

  onLanguageChanged = () => {
    const { t } = this.props

    this.setState({
      presets: buildPresetOptions(t)
    })
  }

  toggleRatioSetting = (ratio) => {
    const {
      cropper,
      updateCropperValues,
      canvas,
      setCropperChangedStatus
    } = this.props
    let newCropper

    if (canvas.width / canvas.height > ratio) {
      const width = canvas.height * ratio
      newCropper = {
        ...cropper,
        width: width,
        height: canvas.height,
        x: canvas.x + (canvas.width - width) * 0.5,
        y: canvas.y,
      }
    } else if (canvas.width / canvas.height < ratio) {
      const height = canvas.width / ratio
      newCropper = {
        ...cropper,
        width: canvas.width,
        height: height,
        x: canvas.x,
        y: canvas.y + (canvas.height - height) * 0.5,
      }
    } else if (canvas.width / canvas.height === ratio) {
      newCropper = {
        ...cropper,
        width: canvas.width,
        height: canvas.height,
        x: canvas.x,
        y: canvas.y,
      }
    }

    updateCropperValues(newCropper)
    setCropperChangedStatus(true)
  }

  toggleSizeSetting = (width, height) => {
    const {
      cropper,
      updateCropperValues,
      canvas,
      setCropperChangedStatus
    } = this.props
    const {
      scaleWidth,
      scaleHeight,
      width: canvasWidth,
      height: canvasHeight
    } = canvas

    const newWidth = Math.min(width * scaleWidth, canvasWidth)
    const newHeight = Math.min(height * scaleHeight, canvasHeight)

    let newCropper = {
      ...cropper,
      width: newWidth,
      height: newHeight,
      x: Math.ceil(canvas.x + (canvas.width - newWidth) / 2),
      y: Math.ceil(canvas.y + (canvas.height - newHeight) / 2)
    }

    updateCropperValues(newCropper)
    setCropperChangedStatus(true)
  }

  toggleCropperLock = () => {
    const { cropper, toggleCropperRatioLock } = this.props
    toggleCropperRatioLock(!cropper.locked)
  }

  toggleCropperSetting = ({ id, w, h, type }) => {
    const {
      setActivePresetById,
      canvas
    } = this.props

    if (
      type === CROPPER_PRESET_TYPES.SIZE &&
      w <= Math.round(canvas.width / canvas.scaleWidth) &&
      h <= Math.round(canvas.height / canvas.scaleHeight)
    ) {
      this.toggleSizeSetting(w, h)
    } else {
      this.toggleRatioSetting(w / h)
    }

    setActivePresetById(id)
  }

  onSizeChange = (value, type) => {
    const {
      cropper,
      updateCropperValues,
      setCropperChangedStatus,
      canvas
    } = this.props

    const pos = type === 'width' ? 'x' : 'y'
    const secondType = type === 'width' ? 'height' : 'width'

    const { scaleWidth, scaleHeight } = canvas

    const scale = type === 'width' ? scaleWidth : scaleHeight

    value = value * scale

    if (value === 0) value = 1

    value = value > canvas[type] - (cropper[pos] - canvas[pos]) ? canvas[type] - (cropper[pos] - canvas[pos]) : +value
    const newCropper = {
      ...cropper,
      activePresetId: 0,
      [type]: value,
    }

    if (cropper.locked) {
      const ratio = cropper.width / cropper.height
      newCropper[secondType] = secondType === 'height' ? value / ratio : value * ratio
      if (cropper[type] === 0 && cropper[secondType] === 0) newCropper[secondType] = newCropper[type]

      if (newCropper.x - canvas.x + newCropper.width > canvas.width) {
        newCropper.width = canvas.width - (newCropper.x - canvas.x)
        newCropper.height = newCropper.width / ratio
        if (newCropper.y - canvas.y + newCropper.height > canvas.height) {
          newCropper.height = canvas.height - (newCropper.y - canvas.y)
          newCropper.width = newCropper.height * ratio
        }
      }
      if (newCropper.y - canvas.y + newCropper.height > canvas.height) {
        newCropper.height = canvas.height - (newCropper.y - canvas.y)
        newCropper.width = newCropper.height * ratio
        if (newCropper.x - canvas.x + newCropper.width > canvas.width) {
          newCropper.width = canvas.width - (newCropper.x - canvas.x)
          newCropper.height = newCropper.width / ratio
        }
      }
    }

    updateCropperValues(newCropper)
    setCropperChangedStatus(true)
  }

  resizeImageData = (imageData, scaleWidth, scaleHeight) => {
    const {
      cropper,
      canvas,
    } = this.props

    const resizeCanvas = document.createElement('canvas')
    resizeCanvas.width = Math.round(cropper.width / scaleWidth)
    resizeCanvas.height = Math.round(cropper.height / scaleHeight)
    const resizeContext = resizeCanvas.getContext('2d')

    // Math.ceil is not appropriate here, because sometimes there are values like this:
    // 1157.8125/0.57890625 === 2000.0000000000002
    resizeContext.putImageData(
      imageData,
      -Math.round((cropper.x - canvas.x) / scaleWidth),
      -Math.round((cropper.y - canvas.y) / scaleHeight),
      Math.round((cropper.x - canvas.x) / scaleWidth),
      Math.round((cropper.y - canvas.y) / scaleHeight),
      Math.round(cropper.width / scaleWidth),
      Math.round(cropper.height / scaleHeight)
    )

    return resizeContext.getImageData(
      0,
      0,
      Math.round(cropper.width / scaleWidth),
      Math.round(cropper.height / scaleHeight)
    )
  }

  onApply = (e, redirect) => {
    const {
      imageData,
      cropper,
      cropperTo,
      updateCropperValues,
      setCropperChangedStatus,
      setCropperModal,
      settingsList,
      currentSettingId,
      addNewSetting,
      updateSettingsIds,
      canvas
    } = this.props

    if (!cropper.changed) return

    const lastSetting = getLastPhysicsSetting(settingsList, currentSettingId)

    const data = lastSetting ? lastSetting.imageData : imageData
    const resizedData = this.resizeImageData(data, canvas.scaleWidth, canvas.scaleHeight)

    const event = {
      event: 'CROP_APPLIED',
      gtm: {
        eventCategory: 'Interactions',
        eventAction: 'click',
        eventLabel: 'CROP_APPLIED'
      },
      amplitude: {
        SAVE_PROPORTION: cropper.locked ? 'YES' : 'NO',
        PRESET_APPLIED: cropper.activePresetId ? 'YES' : 'NO',
      }
    }

    const preset = mapControls.cropper.filter((item) => item.id === cropper.activePresetId)[0]
    if (cropper.activePresetId) event.amplitude.PRESET_NAME = preset.name
    else event.amplitude.PRESET_NAME = null
    sendMetric(event)

    updateSettingsIds(currentSettingId + 1)
    addNewSetting({
      name: 'crop',
      isCommitted: true,
      id: currentSettingId + 1,
      imageData: resizedData,
    })

    const newCropper = {
      ...cropper,
      id: currentSettingId + 1,
    }

    updateCropperValues(newCropper)
    setCropperChangedStatus(false)
    setCropperModal(false)

    if (redirect) {
      this.props.history.push(cropperTo)
    }
  }

  onCloseModal = (e, redirect) => {
    const { cropperTo, setCropperModal } = this.props
    setCropperModal(false)
    if (redirect) this.props.history.push(cropperTo)
  }

  setPortraitOrientation = () => {
    this.setOrientation('portrait')
  }

  setLandscapeOrientation = () => {
    this.setOrientation('landscape')
  }

  setOrientation = (orientation) => {
    const { cropper } = this.props

    const selectedPreset = findPresetById(this.state.presets, cropper.activePresetId)

    if (selectedPreset.name === 'freeform') {
      if (
        (orientation === 'landscape' && cropper.width / cropper.height < 1) ||
        (orientation === 'portrait' && cropper.width / cropper.height > 1)
      ) {
        this.toggleRatioSetting(cropper.height / cropper.width)
      }
    } else {
      if (
        (orientation === 'landscape' && selectedPreset.w / selectedPreset.h > 1) ||
        (orientation === 'portrait' && selectedPreset.w / selectedPreset.h < 1)
      ) {
        this.toggleCropperSetting(selectedPreset)
      } else {
        this.toggleCropperSetting({
          ...selectedPreset,
          w: selectedPreset.h,
          h: selectedPreset.w
        })
      }
    }
  }

  render() {
    const { 
      imageSrc, 
      cropper, 
      showCropperModal, 
      canvas, 
      t 
    } = this.props
    const { 
      scaleWidth, 
      scaleHeight, 
      width, 
      height 
    } = canvas
    const { presets } = this.state

    const selectedPreset = findPresetById(presets, cropper.activePresetId)

    return (
      <div className="cropper-control">
        <div className="cropper-control__title">{t('titles.crop')}</div> 
        <div className="cropper-control__ratio">
          <label className="cropper-control__label">
            {t('controls.ratio')}
          </label>
          <Select
            placeholder={t('selects.select')}
            options={presets}
            value={selectedPreset}
            onChange={this.toggleCropperSetting}
          />
        </div>
        <div className="cropper-control__orientation">
          <label className="cropper-control__label">
            {t('controls.orientation')}
          </label>
          <div className="controls__button-group">
            <Tooltip
              placement="bottom"
              translationKey="titles.portrait"
              isHidden={(selectedPreset.type === CROPPER_PRESET_TYPES.SIZE && selectedPreset.w > selectedPreset.h) ||
                cropper.width === cropper.height}
            >
              <TileButton
                disabled={
                  (selectedPreset.type === CROPPER_PRESET_TYPES.SIZE && selectedPreset.w > selectedPreset.h) ||
                cropper.width === cropper.height
                }
                active={cropper.width / cropper.height < 1}
                onClick={this.setPortraitOrientation}
              >
                <OrientationPortraitIcon />
              </TileButton>
            </Tooltip>
            <Tooltip
              placement="bottom"
              translationKey="titles.landscape"
              isHidden={(selectedPreset.type === CROPPER_PRESET_TYPES.SIZE && selectedPreset.w < selectedPreset.h) ||
                cropper.width === cropper.height}
            >
              <TileButton
                disabled={
                  (selectedPreset.type === CROPPER_PRESET_TYPES.SIZE && selectedPreset.w < selectedPreset.h) ||
                cropper.width === cropper.height
                }
                active={cropper.width / cropper.height > 1}
                onClick={this.setLandscapeOrientation}
              >
                <OrientationLandscapeIcon />
              </TileButton>
            </Tooltip>
          </div>
        </div>
        <div className="cropper-control__size">
          <label className="cropper-control__label" htmlFor="cropper-width">
            {t('controls.width')}
          </label>
          <label className="cropper-control__label" htmlFor="cropper-height">
            {t('controls.height')}
          </label>
          <InputNumeric 
            className="input-text"
            id="cropper-width"
            value={Math.round(cropper.width / scaleWidth)}
            min={1}
            max={Math.round(width / scaleWidth)}
            onChange={(value) => this.onSizeChange(value, 'width')}
            strict
          />
          <IconButton
            onClick={this.toggleCropperLock}
          >
            {cropper.locked ?
              <LockIcon /> :
              <UnlockIcon />
            }
          </IconButton>
          <InputNumeric 
            className="input-text"
            id="cropper-height"
            value={Math.round(cropper.height / scaleHeight)}
            min={1}
            max={Math.round(height / scaleHeight)}
            onChange={(value) => this.onSizeChange(value, 'height')}
            strict
          />
        </div>

        <Button
          block
          disabled={!imageSrc || !cropper.changed}
          onClick={this.onApply}
        >
          {t('buttons.apply')}
        </Button>
        <Modal isOpen={showCropperModal} onCloseModal={this.onCloseModal}>
          <div className="modal__body">{t('modals.image-changes')}</div>
          <div className="modal__buttons">
            <Button variant="ghost" onClick={(e) => this.onCloseModal(e, true)}>
              {t('buttons.no')}
            </Button>
            <div className="modal__buttons-separator" />
            <Button onClick={(e) => this.onApply(e, true)}>
              {t('buttons.yes')}
            </Button>
          </div>
        </Modal>
      </div>
    )
  }
}

const mapStateToProps = ({
  imageStore: { imageSrc, imageData },
  cropperStore: { cropper, showCropperModal, cropperTo },
  canvasStore: { canvas },
  settingsStore: { settingsList, currentSettingId },
}) => ({
  imageSrc,
  imageData,
  cropper,
  showCropperModal,
  cropperTo,
  canvas,
  settingsList,
  currentSettingId,
})
const mapDispatchToProps = (dispatch) => ({
  updateCropperValues: (values) => dispatch(updateCropperValues(values)),
  setCropperActive: (isActive) => dispatch(setCropperActive(isActive)),
  toggleCropperRatioLock: (locked) => dispatch(toggleCropperRatioLock(locked)),
  setActivePresetById: (id) => dispatch(setActivePresetById(id)),
  setCropperChangedStatus: (status) => dispatch(setCropperChangedStatus(status)),
  setCropperModal: (status) => dispatch(setCropperModal(status)),
  addNewSetting: (setting) => dispatch(addNewSetting(setting)),
  updateSettingsIds: (id) => dispatch(updateSettingsIds(id)),
})

export default withTranslation()(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(CropperControl)
)
